import * as React from 'react'
import { Button as AntButton } from 'antd'
import type { ButtonProps as AntButtonProps } from 'antd'

export type ButtonVariant =
  | 'primary'
  | 'outline'
  | 'ghost'
  | 'destructive'
  | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

export interface ButtonProps extends Omit<
  AntButtonProps,
  'type' | 'size' | 'danger' | 'variant'
> {
  variant?: ButtonVariant
  size?: ButtonSize
  asChild?: boolean
  children?: React.ReactNode
  htmlType?: 'button' | 'submit' | 'reset'
}

function variantToAntProps(
  variant: ButtonVariant = 'primary',
): Pick<AntButtonProps, 'type' | 'danger'> {
  switch (variant) {
    case 'primary':
      return { type: 'primary' }
    case 'outline':
      return { type: 'default' }
    case 'ghost':
      return { type: 'text' }
    case 'destructive':
      return { type: 'primary', danger: true }
    case 'link':
      return { type: 'link' }
    default:
      return { type: 'primary' }
  }
}

function sizeToAntSize(size: ButtonSize = 'md'): AntButtonProps['size'] {
  if (size === 'sm') return 'small'
  if (size === 'lg') return 'large'
  return 'middle'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      asChild: _asChild,
      className,
      style,
      htmlType = 'button',
      ...props
    },
    ref,
  ) => {
    const antProps = variantToAntProps(variant)
    const antSize = sizeToAntSize(size)
    const isIcon = size === 'icon'
    const combinedClassName = [
      className,
      variant === 'outline' ? '!bg-white' : ''
    ].filter(Boolean).join(' ')

    return (
      <AntButton
        ref={ref}
        size={antSize}
        shape={isIcon ? 'circle' : 'round'}
        className={combinedClassName}
        style={style}
        htmlType={htmlType}
        {...antProps}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button }
